import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiEye, FiShoppingCart, FiCheck } from 'react-icons/fi';
import SEO from '../components/SEO';
import CheckoutModal from '../components/CheckoutModal';
import { trackTemplateClick, trackPreviewClick, trackModalOpen } from '../utils/analytics';
import './Templates.css';

const templates = [
  {
    id: 1,
    title: 'Loft 360',
    slug: 'loft-360',
    description: 'Modern apartment template with green/earth tones. Perfect for luxury apartment communities with a natural, welcoming aesthetic.',
    category: 'Apartment',
    platform: 'HTML/CSS',
    price: 299,
    previewUrl: 'https://loft360template.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/loft360.webp',
    features: ['Responsive Design', 'Contact Form', 'Floor Plans Section', 'Amenities Gallery', 'Easy Customization'],
    colorScheme: '#2D5A4A'
  },
  {
    id: 2,
    title: 'Wash Brook Residences',
    slug: 'wash-brook',
    description: 'Sophisticated apartment template with navy and gold accents. Elegant design for upscale residential communities.',
    category: 'Apartment',
    platform: 'HTML/CSS',
    price: 299,
    previewUrl: 'https://propertytemplatefresh.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/templateclean.webp',
    features: ['Responsive Design', 'Gallery Lightbox', 'Floor Plans Section', 'Scroll Animations', 'Poppins Font'],
    colorScheme: '#1a365d'
  },
  {
    id: 3,
    title: 'Jaxon Stone',
    slug: 'jaxon-stone',
    description: 'Bold modern apartment template with silver and black theme. Features floor plan filtering and sleek animations.',
    category: 'Apartment',
    platform: 'HTML/CSS',
    price: 299,
    previewUrl: 'https://propertytemplateclean.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/jaxonstone.webp',
    features: ['Floor Plan Tabs', 'Gallery Lightbox', 'Scroll Animations', 'Mobile Menu', 'Poppins Font'],
    colorScheme: '#0a0a0a'
  },
  {
    id: 4,
    title: 'Dallas Personal Injury',
    slug: 'dallas-personal-injury',
    description: 'Professional law firm template with navy and gold color scheme. Perfect for personal injury attorneys and legal practices.',
    category: 'Law Firm',
    platform: 'HTML/CSS',
    price: 299,
    previewUrl: 'https://dallaspersonalinjury.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/silverfoxmedia-legalwebsitetemplates/moderncourthouse.png',
    features: ['Attorney Profiles', 'Practice Areas', 'Case Results', 'Contact Form', 'Testimonials'],
    colorScheme: '#1a365d'
  },
  {
    id: 5,
    title: 'DFW Family Law',
    slug: 'dfw-family-law',
    description: 'Compassionate family law template with teal and burgundy color scheme. Perfect for divorce attorneys and family law practices.',
    category: 'Law Firm',
    platform: 'HTML/CSS',
    price: 299,
    previewUrl: 'https://dfwdivorcelawyers.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/couplegettingadivorce1.webp',
    features: ['Attorney Profiles', 'Practice Areas', 'FAQ Accordion', 'Contact Form', 'Scroll Animations'],
    colorScheme: '#1e5f74'
  },
  {
    id: 6,
    title: 'Apex Legal',
    slug: 'apex-legal',
    description: 'Minimalist corporate law firm template with dramatic black & white design. Modern visual effects and premium feel for high-end firms.',
    category: 'Law Firm',
    platform: 'HTML/CSS',
    price: 349,
    previewUrl: 'https://apexlegal.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/lawoffice1.webp',
    features: ['Modern Animations', 'Scroll Effects', 'Minimalist Design', 'Attorney Profiles', 'Practice Areas'],
    colorScheme: '#000000'
  },
  {
    id: 7,
    title: 'Luxe Realty',
    slug: 'luxe-realty',
    description: 'Minimalist real estate template with black & white design and coral accents. Modern animations and property listings with badges and ribbons.',
    category: 'Real Estate',
    platform: 'HTML/CSS',
    price: 349,
    previewUrl: 'https://minimalistrealestatetemplate.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/realstate-templates/modernluxhome1.webp',
    features: ['Scroll Animations', 'Property Badges', 'Team Section', 'Mortgage Calculator', 'Listing Details'],
    colorScheme: '#FF4D4D'
  },
  {
    id: 8,
    title: 'Crestwood Realty',
    slug: 'crestwood-realty',
    description: 'Traditional real estate template with classic navy & gold design. Perfect for established agents serving older clientele with a warm, trusted aesthetic.',
    category: 'Real Estate',
    platform: 'HTML/CSS',
    price: 299,
    previewUrl: 'https://crestwoodrealtytemplate.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/realstate-templates/traditionalhome1.webp',
    features: ['Classic Design', 'Agent Profile', 'Property Listings', 'Testimonials', 'Contact Form'],
    colorScheme: '#1e3a5f'
  },
  {
    id: 9,
    title: 'Vertex Commercial',
    slug: 'vertex-commercial',
    description: 'Cutting-edge commercial real estate template with minimalist black & white design. Features broker profiles, schedule tour CTAs, and advanced scroll animations.',
    category: 'Commercial Real Estate',
    platform: 'HTML/CSS',
    price: 399,
    previewUrl: 'https://vertexcretemplate.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/realstate-templates/officetoweraerial1.webp',
    features: ['Broker Profiles', 'Schedule Tour CTA', 'Property Filters', 'Advanced Animations', 'Listing Details'],
    colorScheme: '#000000'
  },
  {
    id: 10,
    title: 'Pulse Commercial',
    slug: 'pulse-commercial',
    description: 'Modern commercial real estate template with vibrant neon green & blue accents. Features glowing buttons, gradient badges, and advanced scroll animations.',
    category: 'Commercial Real Estate',
    platform: 'HTML/CSS',
    price: 399,
    previewUrl: 'https://pulsecretemplate.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/realstate-templates/officetoweraerial1.webp',
    features: ['Neon Color Scheme', 'Glow Effects', 'Broker Profiles', 'Schedule Tour CTA', 'Advanced Animations'],
    colorScheme: '#00FF85'
  },
  {
    id: 11,
    title: 'Align Wellness',
    slug: 'align-wellness',
    description: 'Minimalist chiropractic clinic template with purple & gold design. Perfect for family chiropractors, pediatric, and prenatal care practices.',
    category: 'Chiropractic',
    platform: 'HTML/CSS',
    price: 349,
    previewUrl: 'https://alignwellnesschirotemplate.onrender.com/',
    image: 'https://silverfoxmedia.s3.us-east-2.amazonaws.com/Templates/chiropracticclinics/chirooffice.webp',
    features: ['Scroll Animations', 'Services Pages', 'FAQ Accordion', 'Contact Form', 'Schema Markup'],
    colorScheme: '#5B4B6E'
  }
];

const Templates = () => {
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleBuyNow = (template) => {
    trackTemplateClick(template.title, template.price);
    trackModalOpen(template.title, template.price);
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handlePreviewClick = (template) => {
    trackPreviewClick(template.title);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  return (
    <>
      <SEO
        title="Website Templates"
        description="Premium HTML/CSS website templates for apartments, law firms, real estate, and chiropractic clinics. Modern, responsive designs with professional aesthetics ready to customize."
        keywords="website templates, law firm templates, apartment website, HTML CSS templates, legal website design, property website templates, real estate templates, realtor website"
        url="/templates"
      />

      {/* Hero Section */}
      <section className="sfm-templates-hero">
        <div className="sfm-templates-hero__bg">
          <div className="sfm-templates-hero__gradient-orb sfm-templates-hero__gradient-orb--1" />
          <div className="sfm-templates-hero__gradient-orb sfm-templates-hero__gradient-orb--2" />
          <div className="sfm-templates-hero__grid-overlay" />
        </div>
        <div className="container">
          <motion.div
            className="sfm-templates-hero__content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="sfm-templates-hero__label">Premium Templates</span>
            <h1>Website <span className="text-gradient">Templates</span></h1>
            <p>Professional HTML/CSS templates ready to customize for your property. No coding required.</p>
          </motion.div>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="sfm-templates section">
        <div className="container">
          <div className="sfm-templates__grid">
            {templates.map((template, index) => (
              <motion.div
                key={template.id}
                className="sfm-template-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredId(template.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="sfm-template-card__image">
                  <img
                    src={template.image}
                    alt={`${template.title} template preview`}
                    className="sfm-template-card__img"
                  />
                  <div className={`sfm-template-card__overlay ${hoveredId === template.id ? 'active' : ''}`}>
                    <a
                      href={template.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sfm-template-card__btn sfm-template-card__btn--preview"
                      onClick={() => handlePreviewClick(template)}
                    >
                      <FiEye />
                      Live Preview
                    </a>
                    <button
                      onClick={() => handleBuyNow(template)}
                      className="sfm-template-card__btn sfm-template-card__btn--buy"
                    >
                      <FiShoppingCart />
                      Buy Now
                    </button>
                  </div>
                  <div className="sfm-template-card__badge">{template.platform}</div>
                </div>
                <div className="sfm-template-card__content">
                  <div className="sfm-template-card__meta">
                    <span className="sfm-template-card__category">{template.category}</span>
                  </div>
                  <h3 className="sfm-template-card__title">{template.title}</h3>
                  <p className="sfm-template-card__description">{template.description}</p>
                  <ul className="sfm-template-card__features">
                    {template.features.slice(0, 3).map((feature, i) => (
                      <li key={i}>
                        <FiCheck />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="sfm-template-card__footer">
                    <div className="sfm-template-card__price">
                      <span className="sfm-template-card__price-label">Starting at</span>
                      <span className="sfm-template-card__price-value">${template.price}</span>
                    </div>
                    <button
                      onClick={() => handleBuyNow(template)}
                      className="sfm-template-card__cta"
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Services Section */}
          <motion.div
            className="sfm-templates__services"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Template Services</h2>
            <p className="sfm-templates__services-intro">Need help getting your template up and running? We offer full-service setup and hosting.</p>
            <div className="sfm-templates__services-grid">
              <div className="sfm-templates__service-card">
                <h3>Template Only</h3>
                <div className="sfm-templates__service-price">$299</div>
                <p>Download the template files and customize yourself</p>
                <ul>
                  <li><FiCheck /> HTML/CSS/JS Files</li>
                  <li><FiCheck /> All Images Included</li>
                  <li><FiCheck /> Documentation</li>
                  <li><FiCheck /> Free Updates</li>
                </ul>
              </div>
              <div className="sfm-templates__service-card sfm-templates__service-card--featured">
                <span className="sfm-templates__service-badge">Most Popular</span>
                <h3>Full Setup</h3>
                <div className="sfm-templates__service-price">$499</div>
                <p>We customize and launch your site for you</p>
                <ul>
                  <li><FiCheck /> Everything in Template Only</li>
                  <li><FiCheck /> Custom Branding</li>
                  <li><FiCheck /> Content Updates</li>
                  <li><FiCheck /> Domain Setup</li>
                  <li><FiCheck /> SSL Certificate</li>
                </ul>
              </div>
              <div className="sfm-templates__service-card">
                <h3>Hosting & Maintenance</h3>
                <div className="sfm-templates__service-price">$100<span>/mo</span></div>
                <p>We host and maintain your site ongoing</p>
                <ul>
                  <li><FiCheck /> Fast Cloud Hosting</li>
                  <li><FiCheck /> SSL & Security</li>
                  <li><FiCheck /> Monthly Backups</li>
                  <li><FiCheck /> Content Updates</li>
                  <li><FiCheck /> Priority Support</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Checkout Modal */}
      <CheckoutModal
        template={selectedTemplate}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default Templates;
